import {
    AUTH_MODE_FILTERS_OPTIONS,
    AUTH_PROTOCOL_OPTIONS,
    AUTHENTICATOR_TYPES,
    BASELINE_CHANGE_OPTIONS,
    DENY_REASONS,
    DIRECTORY_TYPES,
    DISCOVERY_APPLICATION_TYPES,
    ENTITY_ACTIVITIES_FILTERS_OPTIONS,
    EVENT_LEVEL,
    INCIDENT_STATUS,
    INCIDENT_TYPE,
    RANGE_FILTER_TYPES,
    RETROSPECTIVE_ANSWER,
    RISK_INDICATORS_FILTER_ID,
    RISK_LEVELS,
    SERVICE_ACCOUNT_ACTIONS
} from '@silverfort-nodelibs/common/utils/consts';
import * as filterTypes from './filterTypes';
import { uppercaseFirstLetter } from '../../utils';
import * as externalFilterTypes from './externalFilterTypes';
import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';

const COMMON_DENY_REASONS = [
    'bad-username',
    'bad-password',
    'bad-credentials',
    'unauthorized-workstation',
    'expired-password',
    'account-disabled',
    'expired-account',
    'locked-account',
    'bad-server',
    'account-revoked',
    'expired-ticket',
    'bad-realm',
    'too-big-clock-skew'
];

export const range = {
    id: 'range',
    label: 'Date range',
    filterType: filterTypes.DATE_TIME,
    value: [RANGE_FILTER_TYPES.TODAY.value]
};

export const level = {
    id: 'level',
    label: 'Level',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: Object.values(EVENT_LEVEL),
        allowExclude: true
    },
    defaultValue: '',
    value: ''
};

export const eventSource = {
    id: 'eventSource',
    label: 'Component',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: [],
        searchPlaceholder: 'Search components...',
        allowSearch: true,
        allowExclude: true,
        searchValue: ''
    },
    defaultValue: '',
    value: ''
};

export const domainController = {
    id: 'domainController',
    label: 'Domain Controller',
    filterType: filterTypes.TEXTS,
    defaultValue: [],
    filterConfig: {
        allowExclude: true
    }
};

export const code = {
    id: 'code',
    label: 'Code',
    filterType: filterTypes.TEXTS,
    defaultValue: [],
    value: '',
    filterConfig: {
        allowExclude: true
    }
};

export const description = {
    id: 'description',
    label: 'Description',
    filterType: filterTypes.TEXTS,
    defaultValue: [],
    value: '',
    filterConfig: {
        allowExclude: true
    }
};

export const broadlyUsed = {
    id: 'broadlyUsed',
    label: 'Broadly used',
    filterType: filterTypes.BOOLEAN,
    defaultValue: false,
    value: false,
    isAdvanced: true
};

export const unusedTokens = {
    id: 'unusedTokens',
    label: 'Unused Tokens',
    filterType: filterTypes.BOOLEAN,
    defaultValue: false,
    value: false
};

export const pairedUsers = {
    id: 'pairedUsers',
    label: 'Paired Users',
    filterType: filterTypes.BOOLEAN,
    defaultValue: false,
    value: false
};

export const pairPendingUsers = {
    id: 'pairPendingUsers',
    label: 'Pair-Pending Users',
    filterType: filterTypes.BOOLEAN,
    defaultValue: false,
    value: false
};

export const excludedUsers = {
    id: 'excludedUsers',
    label: 'Excluded Users',
    filterType: filterTypes.BOOLEAN,
    defaultValue: false,
    value: false
};

export const includeDisabledUsers = {
    id: 'includeDisabledUsers',
    label: 'Include Disabled Users',
    filterType: filterTypes.BOOLEAN,
    defaultValue: false,
    value: false
};

export const fromCustomInsight = {
    id: 'fromCustomInsight',
    label: 'From custom insight',
    filterType: filterTypes.BOOLEAN,
    defaultValue: false,
    value: false,
    isAdvanced: true
};

export const highPrivileged = {
    id: 'highPrivileged',
    label: 'Highly privileged',
    filterType: filterTypes.BOOLEAN,
    defaultValue: false,
    value: false,
    isAdvanced: true
};

export const interactiveLogin = {
    id: 'interactiveLogin',
    label: 'Interactive login',
    filterType: filterTypes.BOOLEAN,
    defaultValue: false,
    value: false,
    isAdvanced: true
};

export const newAccounts = {
    id: 'newAccounts',
    label: 'New Accounts',
    filterType: filterTypes.BOOLEAN,
    defaultValue: false,
    value: false,
    isAdvanced: true
};

export const baselineChange = {
    id: 'baselineChange',
    label: 'Baseline Change',
    filterType: filterTypes.RADIO,
    filterConfig: {
        options: BASELINE_CHANGE_OPTIONS
    }
};

export const repetitiveBehavior = {
    id: 'repetitiveBehavior',
    label: 'Repetitive Behavior',
    filterType: filterTypes.BOOLEAN,
    defaultValue: false,
    value: false,
    isAdvanced: true
};

export const suspectedBruteForce = {
    id: 'suspectedBruteForce',
    label: 'Suspected Brute Force',
    filterType: filterTypes.BOOLEAN,
    defaultValue: false,
    value: false,
    isAdvanced: true
};

export const internalServiceAccount = {
    id: 'internalSA',
    label: 'Silverfort Accounts',
    filterType: filterTypes.BOOLEAN,
    defaultValue: false,
    value: false,
    isAdvanced: true
};

export const name = {
    id: 'name',
    label: 'Name',
    filterType: filterTypes.TEXTS,
    defaultValue: [],
    value: '',
    filterConfig: {
        allowExclude: true
    }
};

export const gmsaOnly = {
    id: 'gmsaOnly',
    label: 'gMSA accounts only',
    filterType: filterTypes.BOOLEAN,
    defaultValue: false,
    value: false,
    isAdvanced: true
};

export const protectionStatus = {
    id: 'protectionStatus',
    label: 'Protection status',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: [
            { value: 'true', label: 'Protected Accounts' },
            { value: 'false', label: 'Not Protected' }
        ],
        allowExclude: false
    },
    defaultValue: [],
    value: []
};

export const risk = {
    id: 'risk',
    label: 'Risk',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: [
            { value: RISK_LEVELS.LOW.value, label: uppercaseFirstLetter(RISK_LEVELS.LOW.label) },
            { value: RISK_LEVELS.MEDIUM.value, label: uppercaseFirstLetter(RISK_LEVELS.MEDIUM.label) },
            { value: RISK_LEVELS.HIGH.value, label: uppercaseFirstLetter(RISK_LEVELS.HIGH.label) },
            { value: RISK_LEVELS.CRITICAL.value, label: uppercaseFirstLetter(RISK_LEVELS.CRITICAL.label) }
        ],
        allowExclude: true
    },
    defaultValue: [],
    value: []
};

export const action = {
    id: 'action',
    label: 'Protected Accounts Actions',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: [
            { value: SERVICE_ACCOUNT_ACTIONS.BLOCK.ID, label: SERVICE_ACCOUNT_ACTIONS.BLOCK.DISPLAY },
            {
                value: SERVICE_ACCOUNT_ACTIONS.SEND_TO_SIEM.ID,
                label: SERVICE_ACCOUNT_ACTIONS.SEND_TO_SIEM.DISPLAY
            },
            {
                value: SERVICE_ACCOUNT_ACTIONS.LOG.ID,
                label: SERVICE_ACCOUNT_ACTIONS.LOG.DISPLAY
            }
        ],
        allowExclude: true
    },
    defaultValue: [],
    value: []
};

export const domain = {
    id: 'domain',
    label: 'Domain',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: [],
        searchPlaceholder: 'Search domains...',
        allowSearch: true,
        allowExclude: true,
        searchValue: ''
    },
    defaultValue: [],
    value: []
};

export const policyName = {
    id: 'policyName',
    label: 'Policy name',
    filterType: filterTypes.TEXTS,
    defaultValue: [],
    value: ''
};

export const recentlyUpdated = {
    id: 'recentlyUpdated',
    label: 'Recently updated (7d)',
    filterType: filterTypes.BOOLEAN,
    defaultValue: false,
    value: false
};

export const onlyActivePolicies = {
    id: 'onlyActivePolicies',
    label: 'Active policies only',
    filterType: filterTypes.BOOLEAN,
    defaultValue: false,
    value: false
};

export const protect = {
    id: 'protect',
    label: 'Protect',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: [
            { value: ['ntlm_enabled', 'kerberos_enabled', 'ldap_enabled'], label: 'Active Directory' },
            { value: ['oidc_enabled'], label: 'Entra ID' },
            { value: ['okta_enabled'], label: 'Okta' },
            { value: ['radius_enabled'], label: 'Radius' },
            { value: ['relying_parties_enabled'], label: 'ADFS' },
            { value: ['ping_fed_enabled'], label: 'PingFederate' }
        ]
    },
    defaultValue: [],
    value: []
};

export const policyGroup = {
    id: 'policyGroup',
    label: 'Policy group',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: [],
        searchPlaceholder: 'Search policies groups...',
        allowSearch: true,
        searchValue: ''
    },
    defaultValue: [],
    value: [],
    defaultDescription: 'All'
};

export const usersAndGroups = {
    id: 'usersAndGroups',
    label: 'Users and groups',
    filterType: filterTypes.EXTERNAL,
    externalFilterType: externalFilterTypes.ENTITIES,
    defaultValue: [],
    value: []
};

export const adEntitiesAndLocalAccounts = {
    id: 'adEntitiesAndLocalAccounts',
    label: 'Users and groups',
    filterType: filterTypes.EXTERNAL,
    externalFilterType: externalFilterTypes.AD_ENTITIES_AND_LOCAL_ACCOUNTS,
    defaultValue: [],
    value: []
};

export const resources = {
    id: 'resources',
    label: 'Destination Resources',
    filterType: filterTypes.EXTERNAL,
    externalFilterType: externalFilterTypes.SERVERS,
    defaultValue: [],
    value: []
};

export const application = {
    id: 'application',
    label: 'Application',
    filterType: filterTypes.TEXTS
};

export const applicationType = {
    id: 'applicationType',
    label: 'Application type',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: Object.values(DISCOVERY_APPLICATION_TYPES),
        searchPlaceholder: 'Search application types...',
        allowSearch: true,
        searchValue: ''
    },
    defaultValue: [],
    value: []
};

export const lastUsed = {
    id: 'lastUsed',
    label: 'Last used',
    filterType: filterTypes.DATE_TIME,
    notAllowedRangeTypes: [RANGE_FILTER_TYPES.CUSTOM_RANGE.value]
};

export const users = {
    id: 'users',
    label: 'Users',
    defaultValue: [],
    value: [],
    filterType: filterTypes.EXTERNAL,
    externalFilterType: externalFilterTypes.USERS
};

export const source = {
    id: 'source',
    label: 'Source',
    filterType: filterTypes.EXTERNAL,
    externalFilterType: externalFilterTypes.DEVICES_ONLY,
    defaultValue: [],
    value: []
};
export const destination = {
    id: 'destination',
    label: 'Destination',
    filterType: filterTypes.EXTERNAL,
    externalFilterType: externalFilterTypes.SERVERS_ONLY,
    defaultValue: [],
    value: [],
    allowApplications: true
};

export const EVENTS_FILTERS = {
    [range.id]: range,
    [level.id]: level,
    [eventSource.id]: eventSource,
    [domainController.id]: domainController,
    [code.id]: code,
    [description.id]: description
};

export const accountDomain = {
    id: 'accountDomain',
    label: 'Account domain',
    filterType: filterTypes.TEXTS,
    filterConfig: { allowExclude: true },
    isAdvanced: true
};

export const mfa = {
    id: 'mfa',
    label: 'MFA response',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: [
            { value: 'true', label: 'Approved' },
            { value: 'automatic', label: 'Auto-Response' },
            { value: 'false', label: 'Blocked' },
            { value: 'user-excluded', label: 'Excluded User' },
            { value: 'no-relevant_tokens', label: 'No Relevant Device' },
            { value: 'cant-send-mfa', label: 'Not Available' },
            { value: 'timeout', label: 'Timed Out' },
            { value: 'invalid-token', label: 'Invalid Token' },
            { value: 'unpaired', label: 'Unpaired' },
            { value: 'auto-deny', label: 'Auto-Blocked' },
            { value: 'unprotected', label: 'Unprotected' },
            { value: 'invalid-otp', label: 'Invalid OTP' },
            { value: 'fail-open', label: 'Fail-Open' },
            { value: 'fail-close', label: 'Fail-Close' },
            { value: 'missing-mfa-user-binding', label: 'Unbound' },
            { value: 'invalid-mfa-user-selection', label: 'Invalid User Selection' }
        ],
        allowExclude: true
    },
    isAdvanced: true
};

export const node = {
    id: 'node',
    label: 'Node',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: [],
        searchPlaceholder: 'Search nodes...',
        allowSearch: true,
        searchValue: '',
        allowExclude: true
    },
    isAdvanced: true
};

export const country = {
    id: 'country',
    label: 'Country',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: []
    },
    isAdvanced: true
};

export const idp = {
    id: 'idp',
    label: 'IdP',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: []
    },
    isAdvanced: true
};

export const protocol = {
    id: 'protocol',
    label: 'Protocol',
    filterType: filterTypes.SELECT,
    filterConfig: {
        allowExclude: true,
        options: AUTH_PROTOCOL_OPTIONS
    },
    isAdvanced: true
};

export const alert = {
    id: 'alert',
    label: 'Alert',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: [{ value: 'true', label: 'Has alert' }],
        allowExclude: true
    },
    isAdvanced: true
};

export const internalAuth = {
    id: 'internalAuth',
    label: 'Silverfort Internal Authentication',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: [{ value: 'true', label: 'Internal Authentication by Silverfort' }],
        allowExclude: true
    },
    isAdvanced: true
};

export const result = {
    id: 'result',
    label: 'IdP Result',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: [
            { value: 'true', label: 'Allowed' },
            { value: 'false', label: 'Denied' }
        ],
        allowExclude: true
    },
    isAdvanced: true
};

export const resourceDomain = {
    id: 'resourceDomain',
    label: 'Destination domain',
    filterType: filterTypes.TEXTS,
    filterConfig: { allowExclude: true },
    isAdvanced: true
};

export const riskIndicator = {
    id: RISK_INDICATORS_FILTER_ID,
    label: 'Risk indicator',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: [],
        searchPlaceholder: 'Search risk indicators...',
        allowSearch: true,
        searchValue: '',
        allowExclude: true
    },
    isAdvanced: true
};

export const ruleName = {
    id: 'ruleName',
    label: 'Executed policy',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: [],
        searchPlaceholder: 'Search policy...',
        allowSearch: true,
        searchValue: '',
        allowExclude: true
    },
    isAdvanced: true
};

export const smartPolicies = {
    id: 'smartPolicies',
    label: 'Service Accounts Smart Policy',
    filterType: filterTypes.BOOLEAN,
    isAdvanced: true
};

export const service = {
    id: 'service',
    label: 'Service',
    filterType: filterTypes.TEXTS,
    filterConfig: { allowExclude: true },
    isAdvanced: true
};

export const silverfortAction = {
    id: 'action',
    label: 'Silverfort Action',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: [
            { value: 'allow', label: 'Allow' },
            { value: 'deny', label: 'Deny' },
            { value: 'mfa', label: 'MFA' },
            { value: 'notify', label: 'Notify' }
        ],
        allowExclude: true
    },
    isAdvanced: true
};

export const userType = {
    id: 'userType',
    label: 'Account type',
    defaultValue: [],
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: [
            { value: 1, label: 'User Account' },
            { value: 2, label: 'Machine Account' },
            { value: 3, label: 'Service Account' },
            { value: 4, label: 'Silverfort Account' }
        ],
        allowExclude: true
    },
    isAdvanced: true
};

export const deviceOrServer = {
    id: 'deviceOrServer',
    label: 'Source or destination',
    filterType: filterTypes.TEXTS,
    filterConfig: { allowExclude: true },
    isAdvanced: true
};

export const tokenId = {
    id: 'tokenId',
    label: 'Token ID',
    filterType: filterTypes.TEXTS,
    filterConfig: { allowExclude: true },
    isAdvanced: true
};

export const authenticator = {
    id: 'authenticator',
    label: 'Authenticator',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: Object.values(AUTHENTICATOR_TYPES)
            .filter((authenticatorType) => !authenticatorType.label.startsWith('External -'))
            .sort((at1, at2) => at1.label.localeCompare(at2.label)),
        allowExclude: true
    },
    isAdvanced: true
};

export const directoryType = {
    id: 'directoryType',
    label: 'Auth type',
    defaultValue: [],
    value: [],
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: [
            { value: DIRECTORY_TYPES.ad.key, label: DIRECTORY_TYPES.ad.displayName },
            { value: DIRECTORY_TYPES.aad.key, label: DIRECTORY_TYPES.aad.displayName },
            { value: DIRECTORY_TYPES.adfs.key, label: DIRECTORY_TYPES.adfs.displayName },
            { value: DIRECTORY_TYPES.okta.key, label: DIRECTORY_TYPES.okta.displayName },
            { value: DIRECTORY_TYPES.local.key, label: DIRECTORY_TYPES.local.displayName },
            { value: DIRECTORY_TYPES.pingfed.key, label: DIRECTORY_TYPES.pingfed.displayName },
            { value: DIRECTORY_TYPES.radius.key, label: DIRECTORY_TYPES.radius.displayName }
        ],
        allowExclude: true
    },
    isAdvanced: true
};

export const retrospectiveAnswer = {
    id: 'retrospectiveAnswer',
    label: '"Was it you"? answer',
    defaultValue: [],
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: [
            { value: RETROSPECTIVE_ANSWER.yes.key, label: RETROSPECTIVE_ANSWER.yes.displayName },
            { value: RETROSPECTIVE_ANSWER.no.key, label: RETROSPECTIVE_ANSWER.no.displayName },
            { value: RETROSPECTIVE_ANSWER.pending.key, label: RETROSPECTIVE_ANSWER.pending.displayName },
            { value: RETROSPECTIVE_ANSWER.expired.key, label: RETROSPECTIVE_ANSWER.expired.displayName }
        ]
    },
    isAdvanced: true
};

export const reason = {
    id: 'reason',
    label: 'Deny reasons',
    filterType: filterTypes.SELECT,
    filterConfig: {
        commonOptions: COMMON_DENY_REASONS.map((denyReasonKey) => {
            return { label: DENY_REASONS[denyReasonKey], value: denyReasonKey };
        }),
        options: Object.keys(DENY_REASONS)
            .filter((key) => !COMMON_DENY_REASONS.includes(key))
            .map((denyReasonKey) => {
                return { label: DENY_REASONS[denyReasonKey], value: denyReasonKey };
            }),
        searchPlaceholder: 'Search deny reasons...',
        allowSearch: true,
        searchValue: ''
    },
    defaultValue: [],
    value: [],
    isAdvanced: true
};

export const authMode = {
    id: 'authMode',
    label: 'Mode',
    filterType: filterTypes.SELECT,
    filterConfig: { options: Object.values(AUTH_MODE_FILTERS_OPTIONS) },
    isAdvanced: true
};

export const hideNoLocation = {
    id: 'hideNoLocation',
    label: 'Hide no location',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: [
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' }
        ]
    },
    isAdvanced: true
};

export const location = {
    id: 'location',
    label: 'Authentications with location',
    filterType: filterTypes.BOOLEAN,
    defaultValue: false,
    value: false,
    filterConfig: {
        allowExclude: false
    },
    isAdvanced: true
};

export const activitiesType = {
    id: 'activitiesType',
    label: 'Activities Type',
    tooltip:
        'In intersection with other filters, risk changes records are not affected by authentication fields filters.',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: Object.values(ENTITY_ACTIVITIES_FILTERS_OPTIONS)
    },
    defaultValue: [],
    value: []
};
export const IncidentType = {
    id: 'kind',
    label: 'Type',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: Object.values(INCIDENT_TYPE).map((obj) => {
            return { value: obj.value, label: obj.guiName };
        })
    },
    defaultValue: [],
    value: []
};

export const IncidentStatus = {
    id: 'status',
    label: 'Status',
    filterType: filterTypes.SELECT,
    filterConfig: {
        options: Object.values(INCIDENT_STATUS)
    },
    defaultValue: [],
    value: [INCIDENT_STATUS.ACKNOWLEDGED.value, INCIDENT_STATUS.ONGOING.value, INCIDENT_STATUS.OPEN.value]
};

export const requestID = {
    id: 'requestID',
    label: 'Request ID',
    filterType: filterTypes.TEXTS,
    filterConfig: { allowExclude: false },
    isAdvanced: true
};

export const INCIDENT_FILTERS = {
    [IncidentType.id]: IncidentType,
    [IncidentStatus.id]: IncidentStatus
};

export const SERVICE_ACCOUNTS_FILTERS = {
    [broadlyUsed.id]: broadlyUsed,
    [fromCustomInsight.id]: fromCustomInsight,
    [highPrivileged.id]: highPrivileged,
    [interactiveLogin.id]: interactiveLogin,
    [newAccounts.id]: newAccounts,
    [baselineChange.id]: baselineChange,
    [repetitiveBehavior.id]: repetitiveBehavior,
    [suspectedBruteForce.id]: suspectedBruteForce,
    [internalServiceAccount.id]: internalServiceAccount,
    [name.id]: name,
    [protectionStatus.id]: protectionStatus,
    [risk.id]: risk,
    [action.id]: action,
    [domain.id]: domain,
    [gmsaOnly.id]: gmsaOnly
};

export const POLICIES_FILTERS = {
    [policyName.id]: policyName,
    [recentlyUpdated.id]: recentlyUpdated,
    [onlyActivePolicies.id]: onlyActivePolicies,
    [protect.id]: protect,
    [policyGroup.id]: policyGroup,
    [adEntitiesAndLocalAccounts.id]: adEntitiesAndLocalAccounts,
    [resources.id]: resources
};

export const APP_DISCOVERY_FILTERS = {
    [application.id]: application,
    [directoryType.id]: { ...directoryType, isAdvanced: false },
    [applicationType.id]: applicationType,
    [lastUsed.id]: lastUsed
};

const SAAS_INCOMPATIBLE_FILTER_OPTIONS = {
    [directoryType.id]: [DIRECTORY_TYPES.adfs.key, DIRECTORY_TYPES.pingfed.key, DIRECTORY_TYPES.radius.key],
    [authenticator.id]: [AUTHENTICATOR_TYPES.RADIUS_OTP.value, AUTHENTICATOR_TYPES.PING.value]
};

const getSystemModeCompatibleFilterOptions = (isSaas, filterObj) => {
    if (!isSaas) {
        return filterObj;
    }
    const valuesToRemove = SAAS_INCOMPATIBLE_FILTER_OPTIONS[filterObj.id] || [];
    const isSupportedFilterType = filterObj.filterType === filterTypes.SELECT && valuesToRemove.length;
    if (!isSupportedFilterType) {
        console.error('Trying to convert an unsupported filter config to SaaS compatability, returning initial value');
        return filterObj;
    }
    const convertedFilterObj = {
        ...filterObj,
        filterConfig: {
            ...filterObj.filterConfig,
            options: filterObj.filterConfig.options.filter((option) => !valuesToRemove.includes(option.value))
        }
    };

    return convertedFilterObj;
};

export const getInitialLogsFilter = (systemMode = SYSTEM_MODES.ON_PREM) => {
    const isSaas = systemMode !== SYSTEM_MODES.ON_PREM;
    return {
        [users.id]: users,
        [source.id]: source,
        [destination.id]: destination,
        [range.id]: range,
        [retrospectiveAnswer.id]: retrospectiveAnswer,
        [accountDomain.id]: accountDomain,
        [userType.id]: userType,
        [alert.id]: alert,
        [internalAuth.id]: internalAuth,
        [directoryType.id]: getSystemModeCompatibleFilterOptions(isSaas, directoryType),
        [authenticator.id]: getSystemModeCompatibleFilterOptions(isSaas, authenticator),
        [country.id]: country,
        [reason.id]: reason,
        [resourceDomain.id]: resourceDomain,
        [domainController.id]: { ...domainController, isAdvanced: true },
        [ruleName.id]: ruleName,
        [smartPolicies.id]: smartPolicies,
        [hideNoLocation.id]: hideNoLocation,
        [idp.id]: idp,
        [result.id]: result,
        [mfa.id]: mfa,
        ...(systemMode !== SYSTEM_MODES.SAAS && { [node.id]: node }),
        [authMode.id]: authMode,
        [protocol.id]: protocol,
        [risk.id]: { ...risk, isAdvanced: true },
        [riskIndicator.id]: riskIndicator,
        [service.id]: service,
        [silverfortAction.id]: silverfortAction,
        [deviceOrServer.id]: deviceOrServer,
        [tokenId.id]: tokenId,
        [requestID.id]: requestID
    };
};

export const USER_ENROLLMENT_FILTERS = {
    [unusedTokens.id]: unusedTokens,
    [pairedUsers.id]: pairedUsers,
    [pairPendingUsers.id]: pairPendingUsers,
    [excludedUsers.id]: excludedUsers,
    [includeDisabledUsers.id]: includeDisabledUsers,
    [domain.id]: domain
};

export const ENTITY_ACTIVITIES_FILTERS = {
    [users.id]: users,
    [range.id]: range,
    [source.id]: source,
    [destination.id]: destination,
    [activitiesType.id]: activitiesType,
    [riskIndicator.id]: riskIndicator,
    [risk.id]: {
        ...risk,
        isAdvanced: true,
        label: 'Risk Level',
        tooltip:
            'Risk level filter is based on the authentication risk level and relevant only to authentication records.'
    },
    [location.id]: location,
    action: {
        id: 'action',
        label: 'Silverfort Action',
        filterType: filterTypes.SELECT,
        filterConfig: {
            options: [
                { value: 'allow', label: 'Allow' },
                { value: 'deny', label: 'Deny' },
                { value: 'mfa', label: 'MFA' },
                { value: 'notify', label: 'Notify' }
            ],
            allowExclude: true
        },
        isAdvanced: true
    },
    [result.id]: result,
    [mfa.id]: mfa,
    [reason.id]: reason,
    [protocol.id]: protocol
};
